/**
 * An object representing predicted and actual values.
 * @typedef {Object} Response
 * @property {Object} predictValue - The predicted value.
 * @property {number} predictValue.id - The ID of the predicted value.
 * @property {number} predictValue.year - The year of the predicted value.
 * @property {string} predictValue.period - The period of the predicted value.
 * @property {string} predictValue.value - The predicted value in JSON format.
 * @property {string} predictValue.unique_id - The unique ID of the predicted value.
 * @property {number} predictValue.is_real - The flag indicating whether the predicted value is real.
 * @property {Object} actualValue - The actual value.
 * @property {number} actualValue.id - The ID of the actual value.
 * @property {number} actualValue.year - The year of the actual value.
 * @property {string} actualValue.period - The period of the actual value.
 * @property {string} actualValue.value - The actual value in JSON format.
 * @property {string} actualValue.unique_id - The unique ID of the actual value.
 * @property {number} actualValue.is_real - The flag indicating whether the actual value is real.
 * @property {Object} routeData The route data.
 * @property {number} routeData.id - The ID of the route data.
 * @property {string} routeData.name - The name of the route data.
 * @property {string} routeData.unique_id - The unique ID of the route data.
 * @property {number} routeData.parent - The parent ID of the route data.
 * @property {number} routeData.create_time - The creation time of the route data.
 * @property {number} routeData.modified_time - The modification time of the route data.
 * @property {string} routeData.unit - The unit of the route data.
 * @property {string} routeData.period - The period of the route data.
 * @property {number} routeData.is_active - The flag indicating whether the route data is active.
 * @property {number} routeData.is_deleted - The flag indicating whether the route data is deleted.
 */

/**
 * @typedef {Object} ActualValue
 * @property {number} time
 * @property {number} Actual_value
 * @property {number} Actual_value_with_noise
 * @property {number} Predicted_value
 * @property {number} Accuracy
 * @property {string} Title
 */

import ChartBuilder from "../builders/history.chart.builder";

/**
 * @param {Response} data
 */
export default function DistributionChartDirector(data, dataKey, routeName) {
  const emptyChart = {
    empty: true,
    title: {
      text: 'Lead '+dataKey
    },
    xAxis: {
      visible: false
    },
    yAxis: {
      visible: false
    },
    exporting: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    legend: {
      enabled: false
    },
    tooltip: {
      enabled: false
    },
    series: []
  };
  if(data.volumeData.value == undefined){
    return emptyChart;
  }
  const volumeData = JSON.parse(data.volumeData.value);
  if(volumeData['vol_'+dataKey] === undefined){
    return emptyChart;
  }
  const chartData = volumeData['vol_'+dataKey];
  if(!chartData.length){
    return emptyChart;
  }
  const categoryCharacterLimit = 6;
  const rawCategories = chartData.map(item => item.band);
  const categories = chartData.map(item => {
    const str = String(item.band);
    return str.length > categoryCharacterLimit
      ? str.slice(0, categoryCharacterLimit) + "..."
      : str;
  });
  const lastCategory = categories[categories.length - 1];
  const chart = new ChartBuilder();
  const predictValueInfo = JSON.parse(data.predictValue.value || "{}");
  const predictValues = Object.values(predictValueInfo);
  const predictMoreInfo = JSON.parse(data.predictValue.more_info || "{}");
  const p10values = [predictMoreInfo.P10_1, predictMoreInfo.P10_2, predictMoreInfo.P10_3].map(
    (i) => Number(Number(i)?.toFixed(2))
  );
  const p90values = [predictMoreInfo.P90_1, predictMoreInfo.P90_2, predictMoreInfo.P90_3].map(
    (i) => Number(Number(i)?.toFixed(2))
  );
  const p10value = p10values[dataKey - 1];
  const p90value = p90values[dataKey - 1];
  const foreCastValue = predictValues[dataKey - 1];
  if(p10value === undefined || parseInt(p10value) === -1 || p90value === undefined || parseInt(p90value) === -1){
    return emptyChart;
  }
  const seriesValues = chartData.slice(1).map((item) => Number(Number(item.value)?.toFixed(2)));

  function getXPositionForValue(val, chartData) {
    if (val === undefined) return null;
    const numVal = Number(val);
    const exactIndex = chartData.findIndex(
      item => Number(item.band) === numVal
    );
    if (exactIndex !== -1) return exactIndex;
    for (let i = 0; i < chartData.length - 1; i++) {
      const current = Number(chartData[i].band);
      const next = Number(chartData[i + 1].band);
      const min = Math.min(current, next);
      const max = Math.max(current, next);
      if (numVal >= min && numVal <= max) {
        const range = max - min;
        const progress = (numVal - min) / range;
        return i + progress;
      }
    }
    return null;
  }
  chart.setTitle('Lead '+dataKey, { margin: 30 });
  chart.setSubtitle('');
  chart.setOption({
    yAxis: {
      title: {
        text: 'Count',
      },
    },
  });

  chart.setChartType("column");
  var xTitles = [];
  if(foreCastValue !== undefined){
    xTitles.push('<span style="color:#0B6E4F">Forecast: '+foreCastValue+'</span>');
  }
  if(p10value !== undefined){
    xTitles.push('<span style="color:#880808">P10: '+p10value+'</span>');
  }
  if(p90value !== undefined){
    xTitles.push('<span style="color:#0047AB">P90: '+p90value+'</span>');
  }
  const foreCastPos = getXPositionForValue(foreCastValue, chartData, categories);
  const p10Pos = getXPositionForValue(p10value, chartData, categories);
  const p90Pos = getXPositionForValue(p90value, chartData, categories);
  const plotLines = [];
  if (foreCastPos !== null) {
    plotLines.push({
      color: '#0B6E4F',
      width: 2,
      dashStyle: 'dot',
      value: foreCastPos,
      zIndex: 5
    });
  }
  if (p10Pos !== null) {
    plotLines.push({
      color: '#880808',
      width: 2,
      dashStyle: 'dot',
      value: p10Pos,
      zIndex: 5
    });
  }

  if (p90Pos !== null) {
    plotLines.push({
      color: '#0047AB',
      width: 2,
      dashStyle: 'dot',
      value: p90Pos,
      zIndex: 5
    });
  }
  chart.setOption({
    xAxis: {
      title: {
        text: xTitles.join('<br>'),
      },
      categories,
      type: "category",
      tickmarkPlacement: "on",
      startOnTick: false,
      endOnTick: true,
      alignTicks: false,
      minPadding: 0,
      maxPadding: 0,
      plotLines: plotLines,
    },
  });
  chart.setSeries(
    routeName,
    seriesValues,
    { showInLegend: false }
  );
  chart.setOption({
    tooltip: {
      enabled: true,
      formatter: function () {
        const idx = Math.floor(this.point.x) + 1;
        var category = (rawCategories[idx] === '') ? 0 : rawCategories[idx];
        if(idx === rawCategories.length){
          category = lastCategory;
        }
        return `Band: ${category}<br>Vol ${dataKey}: ${this.y}`;
      }
    }
  });

  chart.setCredits(false);
  chart.setOption({
    userOptions: {
      lastCategory: lastCategory
    }
  });
  chart.setOption({
    plotOptions: {
      column: {
        pointPlacement: "between",
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: 1,
        borderColor: '#000000',
      },
      dataLabels: {
        enabled: false,
      }
    },
  });
  chart.setEvents({
    render(thisChart){
      const thisLastCategory = thisChart.target.options.userOptions.lastCategory;
      if (thisLastCategory === undefined) return;

      const labelContainer = thisChart.target.container.querySelector('.highcharts-xaxis-labels');
      if (!labelContainer) return;

      const labels = labelContainer.children;
      if (labels.length < 2) return;

      const last = labels[labels.length - 1];
      const secondLast = labels[labels.length - 2];

      // Already applied?
      if (last.innerHTML === thisLastCategory.toString()) return;

      // X positions
      const lastX = parseFloat(last.getAttribute('x'));
      const secondLastX = parseFloat(secondLast.getAttribute('x'));
      const newX = lastX + (lastX - secondLastX);

      // Parse transform only if rotate() exists
      const parseTransform = (el) => {
        const t = el.getAttribute('transform') || "";
        const match = t.match(/rotate\((-?\d+(?:\.\d+)?)\s([\d.]+)\s([\d.]+)\)/);
        if (!match) return null;
        return {
          angle: parseFloat(match[1]),
          cx:    parseFloat(match[2]),
          cy:    parseFloat(match[3])
        };
      };

      const lastRot = parseTransform(last);
      const secondLastRot = parseTransform(secondLast);

      let newTransform = last.getAttribute('transform');

      if (lastRot && secondLastRot) {
        // Rotated: extrapolate cx just like x
        const newCx = lastRot.cx + (lastRot.cx - secondLastRot.cx);
        const angle = lastRot.angle;
        const cy = lastRot.cy;

        newTransform = `translate(0,0) rotate(${angle} ${newCx} ${cy})`;
      } else {
        // Not rotated → remove any accidental transform
        newTransform = null;
      }

      // Create new label
      const newLabel = last.cloneNode(true);
      newLabel.setAttribute('x', newX);
      if (newTransform) {
        newLabel.setAttribute('transform', newTransform);
      } else {
        newLabel.removeAttribute('transform');
      }
      newLabel.textContent = thisLastCategory;

      labelContainer.appendChild(newLabel);
    }
  })

  return chart.build();
}
